import { C, type Hex, mix, type Pix } from './pix';
import { drop, heart, snowflake, sprite } from './kit';

/**
 * Small status emblems (about 7×8 on the 32 grid) that a move's icon wears in its bottom-left
 * corner for the status it leaves on a foe: a drop of blood, a drop of poison, a snowflake…
 * The same shapes, larger, are the status icons' pictures.
 */

export const STATUS_TINT: Readonly<Record<string, Hex>> = {
  'snow-daze': '#7ad0f0',
  chill: '#5cc8e8',
  'frost-trap': '#8ad8ff',
  slow: '#8a9ab8',
  haste: '#9ff0c8',
  'snow-pride': '#bfeaff',
  'iron-bell': '#f0c040',
  bleed: '#d42a32',
  'war-cry': '#e8a030',
  poison: '#7ad04a',
  charm: '#ff7ac8',
  silence: '#9a8ab8',
  ward: '#5cc8e8',
  focus: '#f0d070',
  'blood-frenzy': '#e0303a',
  'meal-might': '#e8902a',
  'meal-guard': '#d8b070',
  'meal-feast': '#f0b040',
  sunder: '#c89060',
  shackle: '#8a9098',
  trance: '#c86ab8',
};

/** Draws the emblem of a status with its top-left at (x,y), about 8 px square. */
export function statusEmblem(p: Pix, id: string, x: number, y: number): boolean {
  switch (id) {
    case 'bleed':
      drop(p, x + 4, y + 5, 2.6, C.blood);
      return true;
    case 'poison':
      drop(p, x + 4, y + 5, 2.6, C.poison);
      p.px(x + 4, y + 5, C.poisonDark);
      return true;
    case 'snow-daze':
    case 'chill':
    case 'frost-trap':
      snowflake(p, x + 4, y + 4, 3.5, id === 'chill' ? C.white : C.ice, false);
      return true;
    case 'charm':
      heart(p, x + 4, y + 4, 3, '#ff7ac8');
      return true;
    case 'slow':
      // A heavy weight with a down arrow.
      sprite(p, x, y, ['..sss...', '..s.s...', '.sssss..', 'sssssss.', 'sssssss.', '........'], {
        s: '#8a9ab8',
      });
      p.rect(x + 3, y + 3, 2, 3, '#4a5468');
      return true;
    case 'shackle':
      sprite(p, x, y, ['.sss.sss', 's...s..s', 's..s...s', '.sss.sss'], { s: C.steelMid });
      p.rect(x, y + 5, 8, 2, C.steelDark);
      return true;
    case 'silence':
      p.ell(x + 4, y + 4, 3.5, 3.5, '#9a8ab8');
      p.line(x + 2, y + 2, x + 6, y + 6, C.white).line(x + 6, y + 2, x + 2, y + 6, C.white);
      return true;
    case 'sunder':
      sprite(p, x, y, ['ssssssss', 'sss.ssss', 'ss.sssss', 'sss..sss', '.sssss.s', '..ss.ss.'], {
        s: '#c89060',
      });
      return true;
    case 'haste':
      p.line(x, y + 2, x + 6, y + 2, C.qi)
        .line(x + 2, y + 4, x + 8, y + 4, C.qi)
        .line(x, y + 6, x + 6, y + 6, C.qi);
      return true;
    case 'lifesteal':
      heart(p, x + 4, y + 4, 3.2, C.blood);
      return true;
    default:
      return false;
  }
}

/** A colour for anything with no tint of its own. */
export const fallbackTint = (id: string): Hex => STATUS_TINT[id] ?? mix(C.gold, C.ink, 0.1);
